(() => {
  const RETRY_MS = 50;
  const MAX_RETRIES = 200;
  const IDLE_POLL_MS = 25;
  const IDLE_TIMEOUT_MS = 5000;
  let retries = 0;

  const sleep = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

  const waitForIdle = async (controller) => {
    const deadline = Date.now() + IDLE_TIMEOUT_MS;

    while (controller.busy && Date.now() < deadline) {
      await sleep(IDLE_POLL_MS);
    }

    return !controller.busy;
  };

  const install = () => {
    const controller = window.jrbPromoEntriesController;

    if (!controller) {
      retries += 1;
      if (retries <= MAX_RETRIES) window.setTimeout(install, RETRY_MS);
      return;
    }

    if (controller.__jrbStrictPromoCarrierGuardV3) return;
    controller.__jrbStrictPromoCarrierGuardV3 = true;
    controller.__jrbStrictPromoCarrierGuardV2 = true;
    controller.__jrbPromoCheckoutBypass = false;
    controller.__jrbPromoCheckoutVerificationInFlight = false;
    controller.__jrbPromoSyncQueued = false;
    controller.__jrbPromoSyncQueuedForceFetch = false;

    // Never drop a cart reconciliation request just because another reconciliation
    // is already in progress. This is especially important when the shopper removes
    // the final qualifying merchandise line while another cart refresh is finishing.
    const originalSync = controller.sync.bind(controller);

    controller.sync = async function (forceFetch = false) {
      if (this.busy) {
        this.__jrbPromoSyncQueued = true;
        this.__jrbPromoSyncQueuedForceFetch =
          this.__jrbPromoSyncQueuedForceFetch || Boolean(forceFetch);
        return;
      }

      await originalSync(forceFetch);

      if (this.__jrbPromoSyncQueued) {
        const queuedForceFetch = this.__jrbPromoSyncQueuedForceFetch;
        this.__jrbPromoSyncQueued = false;
        this.__jrbPromoSyncQueuedForceFetch = false;
        window.setTimeout(() => void this.sync(queuedForceFetch), 0);
      }
    };

    // Cart page and cart drawer both emit line-item:change:end only after Shopify
    // has accepted the quantity/removal change. Re-fetch the server cart immediately
    // on that event so PROMO-ENTRIES is removed as soon as the last qualifying item
    // disappears instead of waiting for a later page navigation or reload.
    const originalOnCartEvent = controller.onCartEvent.bind(controller);

    controller.onCartEvent = function (event) {
      if (event?.detail?.source === 'jrb-promo-entries') return;

      if (event?.type === 'line-item:change:end') {
        const cart = event?.detail?.cart && Array.isArray(event.detail.cart.items)
          ? event.detail.cart
          : null;

        if (cart) this.cart = cart;
        clearTimeout(this.syncTimer);
        this.syncTimer = window.setTimeout(() => void this.sync(true), 0);
        return;
      }

      originalOnCartEvent(event);
    };

    // Dispatch the refresh directly on document because the theme's Cart and
    // CartDrawer controllers subscribe there. This keeps both cart surfaces in
    // sync immediately after PROMO-ENTRIES is removed server-side.
    controller.notifyRefresh = function () {
      document.dispatchEvent(new CustomEvent('cart:refresh', {
        detail: { cart: this.cart, source: 'jrb-promo-entries' }
      }));

      [120, 350, 800].forEach((delay) => window.setTimeout(() => {
        this.decoratePromoLines();
        this.patchFreeEntriesController();
      }, delay));
    };

    controller.isCheckoutStateInvalid = function () {
      const campaign = this.selectedCampaign();
      const promoItems = this.promoItems();

      if (!campaign) return promoItems.length > 0;
      if (this.busy || !this.cart) return true;

      const qualifyingItems = this.qualifyingItems();
      if (!qualifyingItems.length) return promoItems.length > 0;

      return promoItems.length !== 1 || !this.itemMatchesCampaign(promoItems[0], campaign);
    };

    controller.isFreshCheckoutStateValid = function () {
      const campaign = this.selectedCampaign();
      const promoItems = this.promoItems(this.cart);
      const qualifyingItems = this.qualifyingItems(this.cart);

      if (!campaign || !qualifyingItems.length) {
        return promoItems.length === 0;
      }

      return promoItems.length === 1 && this.itemMatchesCampaign(promoItems[0], campaign);
    };

    controller.verifyPromoCarrierBeforeCheckout = async function () {
      if (this.__jrbPromoCheckoutVerificationInFlight) return false;
      this.__jrbPromoCheckoutVerificationInFlight = true;

      try {
        if (!(await waitForIdle(this))) {
          throw new Error('Timed out waiting for promotional entry cart reconciliation.');
        }

        await this.sync(true);

        if (!(await waitForIdle(this))) {
          throw new Error('Timed out while reconciling promotional entries.');
        }

        this.cart = await this.fetchCart();

        if (!this.isFreshCheckoutStateValid()) {
          await this.sync(true);

          if (!(await waitForIdle(this))) {
            throw new Error('Timed out during the final promotional entry reconciliation.');
          }

          this.cart = await this.fetchCart();
        }

        return this.isFreshCheckoutStateValid();
      } catch (error) {
        console.error('[JRB PROMO-ENTRIES] Checkout verification failed.', error);
        return false;
      } finally {
        this.__jrbPromoCheckoutVerificationInFlight = false;
        this.decoratePromoLines();
        this.updateCheckoutState();
      }
    };

    controller.resumeVerifiedCheckout = function (button, form) {
      if (this.__jrbPromoCheckoutBypass) return;

      this.__jrbPromoCheckoutBypass = true;

      try {
        if (button?.isConnected && !button.disabled) {
          button.click();
          return;
        }

        if (form?.isConnected && typeof form.requestSubmit === 'function') {
          const fallbackButton = form.querySelector('button[name="checkout"]');
          if (!fallbackButton || fallbackButton.disabled) return;
          form.requestSubmit(fallbackButton);
        }
      } finally {
        window.setTimeout(() => {
          this.__jrbPromoCheckoutBypass = false;
        }, 0);
      }
    };

    controller.handleVerifiedCheckoutAttempt = function (event, button, form) {
      if (this.__jrbPromoCheckoutBypass) return;

      const campaign = this.selectedCampaign();
      if (!campaign) return;

      event.preventDefault();
      event.stopPropagation();

      if (this.__jrbPromoCheckoutVerificationInFlight) return;

      void this.verifyPromoCarrierBeforeCheckout().then((valid) => {
        if (!valid) {
          this.scheduleSync(null, true);
          return;
        }

        this.resumeVerifiedCheckout(button, form);
      });
    };

    controller.onCheckoutClick = function (event) {
      const button = event.target.closest?.('button[name="checkout"]');
      if (!button) return;
      this.handleVerifiedCheckoutAttempt(event, button, button.form || button.closest('form'));
    };

    controller.onCheckoutSubmit = function (event) {
      const form = event.target instanceof HTMLFormElement ? event.target : null;
      const button = event.submitter?.matches?.('button[name="checkout"]')
        ? event.submitter
        : form?.querySelector?.('button[name="checkout"]');

      if (!button) return;
      this.handleVerifiedCheckoutAttempt(event, button, form || button.form);
    };

    const originalUpdateCheckoutState = controller.updateCheckoutState.bind(controller);

    controller.updateCheckoutState = function () {
      originalUpdateCheckoutState();

      const campaign = this.selectedCampaign();
      const ready = Boolean(campaign) && !this.isCheckoutStateInvalid();

      document.documentElement.classList.toggle('jrb-promo-entries-checkout-ready', ready);

      if (!campaign) {
        document.documentElement.classList.remove('jrb-promo-entries-checkout-ready');
      }
    };

    controller.updateCheckoutState();
    controller.scheduleSync(null, true);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', install, { once: true });
  } else {
    install();
  }

  document.addEventListener('shopify:section:load', install);
})();
